import { useState, ChangeEvent, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Button,
  Link,
  Image,
  useToast,
  Flex,
  Heading,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { isEmail } from 'validator';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../constants/FrontendPathConstants';

import {
  saveAccessExpiryFromToken,
  startSessionWatchdogs,
  apiFetch,
  setupTabCloseAutoLogout,
} from './session';

interface LoginForm {
  email: string;
  password: string;
}

interface LoginErrors {
  email: string;
  password: string;
}

export const LoginPage = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<LoginErrors>({
    email: '',
    password: '',
  });
  const [isSubmitting, setSubmitting] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');

    if (message === 'password_reset_success') {
      toast({
        title: 'Password Reset Successful!',
        description: 'Please login with your new password',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate(location.pathname, { replace: true });
    } else if (message === 'signup_success') {
      toast({
        title: 'Account Created Successfully!',
        description: 'Your password has been set. Please login to continue.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate(location.pathname, { replace: true });
    } else if (message === 'verification_sent') {
      toast({
        title: 'Verification Email Sent',
        description: 'Please check your inbox and click the verification link before logging in.',
        status: 'info',
        duration: 8000,
        isClosable: true,
      });
      navigate(location.pathname, { replace: true });
    } else if (message === 'email_verified') {
      toast({
        title: 'Email Verified!',
        description: 'Your email has been verified. You can now log in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, toast]);

  const validate = (): boolean => {
    const nextErrors: LoginErrors = { email: '', password: '' };
    let valid = true;

    if (!formData.email) {
      nextErrors.email = 'Email is required';
      valid = false;
    } else if (!isEmail(formData.email)) {
      nextErrors.email = 'Enter a valid email';
      valid = false;
    }

    if (!formData.password) {
      nextErrors.password = 'Password is required';
      valid = false;
    }

    setErrors(nextErrors);
    return valid;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear verification alert when user changes email
    if (name === 'email') {
      setShowVerificationAlert(false);
    }
  };

  const handleResendVerification = async (): Promise<void> => {
    if (!formData.email || !isEmail(formData.email)) {
      toast({
        title: 'Enter your email',
        description: 'Please enter your email address to resend verification',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsResendingVerification(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });

      if (res.ok) {
        toast({
          title: 'Verification email sent!',
          description: 'Please check your inbox and click the verification link.',
          status: 'success',
          duration: 6000,
          isClosable: true,
        });
        setShowVerificationAlert(false);
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to resend verification email');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to resend verification email',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleLogin = async (): Promise<void> => {
    if (!validate()) {
      toast({
        title: 'Validation error',
        description: 'Fix the highlighted fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (!res.ok) {
        let errorMessage = 'Login failed. Please try again';
        let isEmailNotVerified = false;

        try {
          const errorData = await res.json();
          console.log('Login error response:', errorData);

          if (res.status === 403 && errorData.detail?.toLowerCase().includes('not verified')) {
            isEmailNotVerified = true;
            errorMessage = 'Please verify your email before logging in';
          } else if (res.status === 401) {
            errorMessage = 'Invalid email or password';
          } else if (res.status === 422) {
            if (errorData.details && Array.isArray(errorData.details)) {
              errorMessage = errorData.details[0] || 'Please check your input';
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          } else if (res.status === 400) {
            errorMessage = 'Please check your credentials';
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          try {
            const textResponse = await res.text();
            if (textResponse) {
              errorMessage = textResponse;
            }
          } catch (textError) {
            console.error('Error parsing response:', textError);
          }
        }

        if (isEmailNotVerified) {
          setShowVerificationAlert(true);
        }

        throw new Error(errorMessage);
      }

      const tokens = await res.json();
      localStorage.setItem('access_token', tokens.access_token);
      localStorage.setItem('id_token', tokens.id_token);
      localStorage.setItem('isAuthenticated', 'true');

      saveAccessExpiryFromToken();
      startSessionWatchdogs();
      setupTabCloseAutoLogout();

      console.log('Fetching user profile with token:', localStorage.getItem('access_token')?.substring(0, 20) + '...');

      await new Promise(resolve => setTimeout(resolve, 100));

      const userRes = await apiFetch(`${import.meta.env.VITE_API_URL}/api/v1/users/me`);
      console.log('User profile response status:', userRes.status);

      if (!userRes.ok) {
        console.warn('User profile fetch failed, but continuing with login');
        const minimalUserData = {
          email: formData.email,
          first_name: '',
          last_name: '',
          phone: '',
          username: formData.email
        };
        localStorage.setItem('userData', JSON.stringify(minimalUserData));
      } else {
        const userData = await userRes.json();
        console.log('User profile data:', userData);
        localStorage.setItem('userData', JSON.stringify(userData));
      }

      window.dispatchEvent(new Event('auth-changed'));

      toast({
        title: 'Login successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(ROUTES.DASHBOARD, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('isAuthenticated');

      let errorTitle = 'Login failed';
      let errorDescription = 'Invalid email or password';

      if (err instanceof Error) {
        errorDescription = err.message;

        if (err.message.includes('password') || err.message.includes('Password')) {
          errorTitle = 'Password Error';
        } else if (err.message.includes('email') || err.message.includes('Email')) {
          errorTitle = 'Email Error';
        } else if (err.message.includes('validation') || err.message.includes('Validation')) {
          errorTitle = 'Validation Error';
        } else if (err.message.includes('network') || err.message.includes('Network')) {
          errorTitle = 'Network Error';
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (): Promise<void> => {
    if (!formData.email) {
      toast({
        title: 'Enter your email first',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });

      if (!res.ok) {
        let errorMessage = 'Request failed';

        try {
          const errorData = await res.json();
          console.log('Forgot password error response:', errorData);

          if (res.status === 404) {
            errorMessage = 'User not found';
          } else if (res.status === 422) {
            if (errorData.details && Array.isArray(errorData.details)) {
              errorMessage = errorData.details[0] || 'Please check your input';
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          try {
            const textResponse = await res.text();
            if (textResponse) {
              errorMessage = textResponse;
            }
          } catch (textError) {
            console.error('Error parsing response:', textError);
          }
        }

        throw new Error(errorMessage);
      }

      toast({
        title: 'Reset link sent',
        description: 'Check your inbox',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      if (err instanceof Error && err.message === 'User not found') {
        toast({
          title: 'User not found',
          description: 'Please check your email or sign up',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Could not send reset link',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="surface.300"
      position="relative"
      p={4}
    >
      {/* Logo at top */}
      <Box position="absolute" top={6} left="50%" transform="translateX(-50%)">
        <Image
          src="/images/UGenomeHorizontalBlue.png"
          alt="UGenome Logo"
          h="60px"
          objectFit="contain"
        />
      </Box>

      {/* Login Card */}
      <Box
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="16px"
        boxShadow="0 4px 20px rgba(26, 18, 98, 0.1)"
        w={{ base: '100%', sm: '440px' }}
        maxW="440px"
      >
        <VStack spacing={8}>
          {/* Header */}
          <VStack spacing={2}>
            <Heading size="lg" color="primary.500" textAlign="center">
              Welcome Back
            </Heading>
            <Text color="muted.400" fontSize="sm" textAlign="center">
              Sign in to continue to Seq4Life
            </Text>
          </VStack>

          {/* Success Messages */}
          {location.search.includes('message=password_reset_success') && (
            <Box
              w="100%"
              p={3}
              bg="green.50"
              borderRadius="8px"
              border="1px solid"
              borderColor="green.200"
            >
              <Text fontSize="sm" color="green.700" textAlign="center">
                ✓ Your password has been reset successfully
              </Text>
            </Box>
          )}

          {location.search.includes('message=signup_success') && (
            <Box
              w="100%"
              p={3}
              bg="green.50"
              borderRadius="8px"
              border="1px solid"
              borderColor="green.200"
            >
              <Text fontSize="sm" color="green.700" textAlign="center">
                ✓ Account created successfully! Please login
              </Text>
            </Box>
          )}

          {/* Email Verification Alert */}
          {showVerificationAlert && (
            <Alert status="warning" borderRadius="8px">
              <AlertIcon />
              <Box flex="1">
                <AlertDescription fontSize="sm">
                  Your email is not verified. Please check your inbox for the verification link.
                  <Button
                    variant="link"
                    colorScheme="orange"
                    size="sm"
                    ml={2}
                    onClick={handleResendVerification}
                    isLoading={isResendingVerification}
                    loadingText="Sending..."
                  >
                    Resend verification email
                  </Button>
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Form */}
          <VStack spacing={5} w="100%">
            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                Email Address
              </FormLabel>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john.doe@email.com"
                size="lg"
                value={formData.email}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                Password
              </FormLabel>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                size="lg"
                value={formData.password}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>

            <Flex w="100%" justify="flex-end">
              <Link
                fontSize="sm"
                color="primary.500"
                fontWeight="medium"
                onClick={handleForgotPassword}
                _hover={{ color: 'muted.400' }}
              >
                Forgot password?
              </Link>
            </Flex>

            <Button
              w="100%"
              size="lg"
              variant="primary"
              onClick={handleLogin}
              isLoading={isSubmitting}
              loadingText="Signing in..."
            >
              Sign In
            </Button>
          </VStack>

          {/* Footer */}
          <Text fontSize="sm" color="muted.400">
            Don't have an account?{' '}
            <Link
              color="primary.500"
              fontWeight="semibold"
              onClick={() => navigate(ROUTES.SIGNUP)}
              _hover={{ textDecoration: 'underline' }}
            >
              Sign Up
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};
